import { useState, FormEvent } from 'react';
import Layout from '../../components/base/Layout';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    product: '',
    quantity: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5vv001r44f5krorlcu0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          phone: '',
          email: '',
          product: '',
          quantity: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[35vh] md:h-[40vh] min-h-[280px] md:min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20business%20meeting%20room%20with%20large%20windows%20overlooking%20green%20agricultural%20fields%20professional%20corporate%20environment%20warm%20natural%20lighting%20elegant%20interior&width=1920&height=800&seq=200&orientation=landscape"
            alt="Contact Us"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/70 via-emerald-900/50 to-emerald-900/80"></div>
        </div>
        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <span className="inline-block px-4 py-1.5 bg-emerald-500/20 backdrop-blur-sm text-emerald-300 text-xs md:text-sm font-medium rounded-full mb-4 md:mb-6">
            Get In Touch
          </span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
            Contact <span className="bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_auto] animate-gradient-text bg-clip-text text-transparent">Us</span>
          </h1>
          <p className="text-sm md:text-lg text-white/80 max-w-2xl mx-auto px-4">
            Ready to source premium Egyptian produce? Our export team is here to help.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-10 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8 md:gap-12">
            {/* Contact Info */}
            <div className="space-y-6 md:space-y-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">
                  Let&apos;s Start a <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_auto] animate-gradient-text bg-clip-text text-transparent">Conversation</span>
                </h2>
                <p className="text-sm md:text-base text-gray-600">
                  Our team is available 24/7 to answer your inquiries about our products, pricing, and export
                  services.
                </p>
              </div>

              <div className="space-y-4 md:space-y-6">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg md:rounded-xl flex-shrink-0 relative overflow-hidden group cursor-pointer">
                    <span className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-500 bg-[length:200%_200%] animate-gradient-move"></span>
                    <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className="ri-map-pin-line text-lg md:text-xl text-white relative z-10"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base mb-2">Our Locations</h3>
                    <div className="space-y-2">
                      <p className="text-gray-600 text-xs md:text-sm">
                        <strong>Headquarters:</strong> The company&apos;s headquarters, the first branch, Tanta, Al-Bahr Street, Town Mall, administration headquarters, sixth floor.
                      </p>
                      <p className="text-gray-600 text-xs md:text-sm">
                        <strong>Packing Station:</strong> Al-Tawfiqiya, El Beheira – Potato & Orange Packing Station.
                      </p>
                      <p className="text-gray-600 text-xs md:text-sm">
                        <strong>Saudi Arabia:</strong> Riyadh – Al-Riyadh & Al-Tameer Market, Al-Aziziyah, Kingdom of Saudi Arabia
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg md:rounded-xl flex-shrink-0 relative overflow-hidden group cursor-pointer">
                    <span className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-500 bg-[length:200%_200%] animate-gradient-move"></span>
                    <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className="ri-phone-line text-lg md:text-xl text-white relative z-10"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base mb-2">Phone Numbers</h3>
                    <a
                      href="tel:+201069539609"
                      className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                    >
                      +20 106 953 9609
                    </a>
                    <a
                      href="tel:+201066880880"
                      className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                    >
                      +20 106 688 0880
                    </a>
                    <a
                      href="tel:+966548976193"
                      className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                    >
                      +966 548 976 193
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg md:rounded-xl flex-shrink-0 relative overflow-hidden group cursor-pointer">
                    <span className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-500 bg-[length:200%_200%] animate-gradient-move"></span>
                    <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className="ri-mail-line text-lg md:text-xl text-white relative z-10"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 md:mb-2 text-sm md:text-base">Email Addresses</h3>
                    <div className="space-y-1.5 md:space-y-2">
                      <div>
                        <p className="text-[10px] md:text-xs font-medium text-emerald-600 uppercase">Info</p>
                        <a
                          href="mailto:info@al-amein.com"
                          className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                        >
                          info@al-amein.com
                        </a>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-xs font-medium text-emerald-600 uppercase">Sales</p>
                        <a
                          href="mailto:mangersales@al-amein.com"
                          className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                        >
                          mangersales@al-amein.com
                        </a>
                        <a
                          href="mailto:sales@al-amein.com"
                          className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                        >
                          sales@al-amein.com
                        </a>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-xs font-medium text-emerald-600 uppercase">Administration</p>
                        <a
                          href="mailto:amrayoub@al-amein.com"
                          className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                        >
                          amrayoub@al-amein.com
                        </a>
                        <a
                          href="mailto:hosamayoub@al-amein.com"
                          className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                        >
                          hosamayoub@al-amein.com
                        </a>
                      </div>
                      <div>
                        <p className="text-[10px] md:text-xs font-medium text-emerald-600 uppercase">Operations</p>
                        <a
                          href="mailto:operationmanger@al-amein.com"
                          className="text-gray-600 text-xs md:text-sm block hover:text-emerald-600 transition-colors"
                        >
                          operationmanger@al-amein.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-lg md:rounded-xl flex-shrink-0 relative overflow-hidden group cursor-pointer">
                    <span className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-emerald-500 bg-[length:200%_200%] animate-gradient-move"></span>
                    <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className="ri-whatsapp-line text-lg md:text-xl text-white relative z-10"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm md:text-base">WhatsApp</h3>
                    <a
                      href="https://wa.me/201069539609"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 text-xs md:text-sm mt-1 block hover:text-emerald-600 transition-colors"
                    >
                      Chat with us directly
                    </a>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 md:mb-4 text-sm md:text-base">Follow Us</h3>
                <div className="flex gap-2 md:gap-3">
                  <a
                    href="https://web.facebook.com/profile.php?id=100091249341012"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full cursor-pointer relative overflow-hidden group bg-white/80 backdrop-blur-md border border-gray-200"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className="ri-facebook-fill relative z-10 text-gray-600 group-hover:text-white transition-colors"></i>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/al-amein-for-import-export-and-agricultural-investement/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full cursor-pointer relative overflow-hidden group bg-white/80 backdrop-blur-md border border-gray-200"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className="ri-linkedin-fill relative z-10 text-gray-600 group-hover:text-white transition-colors"></i>
                  </a>
                  <a
                    href="https://wa.me/201069539609"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full cursor-pointer relative overflow-hidden group bg-white/80 backdrop-blur-md border border-gray-200"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-400 bg-[length:200%_100%] animate-gradient-move opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    <i className="ri-whatsapp-line relative z-10 text-gray-600 group-hover:text-white transition-colors"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form
                id="contact-inquiry-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-gradient-to-br from-gray-50 to-emerald-50/30 rounded-2xl md:rounded-3xl p-5 sm:p-8 md:p-10 border border-gray-100"
              >
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 md:mb-6">Request a Quote</h3>

                <div className="grid sm:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                  <div>
                    <label htmlFor="name" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-xs md:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your company"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-xs md:text-sm"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                  <div>
                    <label htmlFor="email" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-xs md:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 234 567 890"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-xs md:text-sm"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
                  <div>
                    <label htmlFor="product" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Product Interest
                    </label>
                    <select
                      id="product"
                      name="product"
                      value={formData.product}
                      onChange={handleChange}
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-xs md:text-sm cursor-pointer"
                    >
                      <option value="">Select a product</option>
                      <option value="Spring Onion">Spring Onion</option>
                      <option value="Red Onion">Red Onion</option>
                      <option value="Golden Onions">Golden Onions</option>
                      <option value="Egyptian Sweet Potatoes">Egyptian Sweet Potatoes</option>
                      <option value="Egyptian Carrots">Egyptian Carrots</option>
                      <option value="Egyptian Lemons">Egyptian Lemons</option>
                      <option value="Garlic">Garlic</option>
                      <option value="Pomegranate">Pomegranate</option>
                      <option value="Egyptian Oranges">Egyptian Oranges</option>
                      <option value="White Corn">White Corn</option>
                      <option value="White Beans">White Beans</option>
                      <option value="Multiple Products">Multiple Products</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="quantity" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Estimated Quantity
                    </label>
                    <input
                      type="text"
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      placeholder="e.g., 20 MT"
                      className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-xs md:text-sm"
                    />
                  </div>
                </div>

                <div className="mb-4 md:mb-6">
                  <label htmlFor="message" className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your requirements..."
                    rows={4}
                    maxLength={500}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-white border border-gray-200 rounded-lg md:rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all resize-none text-xs md:text-sm"
                  ></textarea>
                  <p className="text-[10px] md:text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="mb-4 md:mb-6 p-3 md:p-4 bg-emerald-50 border border-emerald-200 rounded-lg md:rounded-xl text-emerald-800 text-xs md:text-sm flex items-center gap-2 md:gap-3">
                    <i className="ri-check-line text-lg md:text-xl"></i>
                    Thank you! Your inquiry has been submitted. We&apos;ll get back to you within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg md:rounded-xl text-red-800 text-xs md:text-sm flex items-center gap-2 md:gap-3">
                    <i className="ri-error-warning-line text-lg md:text-xl"></i>
                    Something went wrong. Please try again or contact us directly.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 md:px-8 py-3 md:py-4 text-white text-sm md:text-base font-semibold rounded-lg md:rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer relative overflow-hidden group"
                >
                  <span className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_100%] animate-gradient-move"></span>
                  <span className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                  <span className="relative z-10 flex items-center gap-2">
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Send Inquiry
                      </>
                    )}
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-10 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
              Our <span className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-500 bg-[length:200%_auto] animate-gradient-text bg-clip-text text-transparent">Location</span>
            </h2>
          </div>
          <div className="rounded-2xl md:rounded-3xl overflow-hidden shadow-lg h-[300px] md:h-[400px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3418.2!2d31.0!3d30.78!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14f7c9f0a0a0a0a0%3A0x0!2sTown%20Mall%2C%20Al-Bahr%20St%2C%20Tanta!5e0!3m2!1sen!2seg!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Al-Amein Location - Tanta, Al-Bahr Street, Town Mall"
            ></iframe>
          </div>
        </div>
      </section>
    </Layout>
  );
}
